import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PAGE_NAME } from "../types";
import AppLayout from "./AppLayout";
import LoadingIndicator from "../WebAppsSharedStuffs/components/LoadingIndicator";
import useEnsureUser from "../WebAppsSharedStuffs/hooks/useEnsureUser";
import {
  isAllowedDomain,
  UnsupportedDomainCard,
} from "../WebAppsSharedStuffs/components/DomainGuard";

interface AuthRouteProps {
  children: React.ReactNode;
}

/**
 * Wraps a page that needs a signed-in user. Signs in silently if needed and
 * then renders the requested page in place — refreshing /WALLET stays on
 * /WALLET instead of bouncing to Home.
 */
const AuthRoute: React.FC<AuthRouteProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { status, retry } = useEnsureUser();

  useEffect(() => {
    if (status === "guest") {
      navigate(PAGE_NAME.LOGIN, { replace: true, state: { from: location.pathname } });
    }
  }, [status, navigate, location.pathname]);

  if (!isAllowedDomain()) {
    return <UnsupportedDomainCard />;
  }

  if (status === "authenticated") {
    return <AppLayout>{children}</AppLayout>;
  }

  if (status === "error") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6 text-center">
        <p className="text-gray-600 text-sm">Connection problem. Check your network and try again.</p>
        <button
          onClick={retry}
          className="px-5 py-2.5 rounded-xl bg-indigo-600 text-white font-semibold text-sm"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <LoadingIndicator />
    </div>
  );
};

export default AuthRoute;
